import React, { useState } from "react";
import { environment } from "environment/environment";
import MerchantLoader from "./MerchantLoader";
import {
  LabelFormatId,
  LabelFormatOption,
  mergeLabelFormats,
} from "./labelFormats";

type SaveStatus = "idle" | "saving" | "saved" | "error";

interface LabelSizePickerProps {
  platform: string;
  shop: string;
  session: string;
  format: LabelFormatId;
  formats: LabelFormatOption[];
  onFormatChange: (next: LabelFormatId) => void;
  onFormatsChange?: (next: LabelFormatOption[]) => void;
}

const LabelSizePicker: React.FC<LabelSizePickerProps> = ({
  platform,
  shop,
  session,
  format,
  formats,
  onFormatChange,
  onFormatsChange,
}) => {
  const [status, setStatus] = useState<SaveStatus>("idle");
  const options = mergeLabelFormats(formats, format);

  const save = async (next: LabelFormatId) => {
    const previous = format;
    onFormatChange(next);
    setStatus("saving");
    try {
      const params = new URLSearchParams({ session });
      if (shop) {
        params.set("shop", shop);
      }
      const response = await fetch(
        `${environment.api_url}/api/v1/integrations/${platform}/settings?${params.toString()}`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ labelFormat: next }),
        }
      );
      const body = await response.json();
      if (!response.ok || body.success === false) {
        throw new Error(body.error || "Could not save label format");
      }
      const saved = (body.data || {}) as {
        labelFormat?: LabelFormatId;
        labelFormats?: LabelFormatOption[];
      };
      const resolved = saved.labelFormat || next;
      onFormatChange(resolved);
      if (onFormatsChange) {
        onFormatsChange(mergeLabelFormats(saved.labelFormats, resolved));
      }
      setStatus("saved");
    } catch {
      onFormatChange(previous);
      setStatus("error");
    }
  };

  return (
    <>
      <div className="zulu-plugin__label-formats" role="radiogroup" aria-label="Label size">
        {options.map((option) => {
          const selected = format === option.id;
          return (
            <label
              key={option.id}
              className={`zulu-plugin__label-format${selected ? " is-selected" : ""}${
                option.recommended ? " is-recommended" : ""
              }`}
            >
              <input
                type="radio"
                name="label-format"
                value={option.id}
                checked={selected}
                disabled={status === "saving"}
                onChange={() => save(option.id)}
              />
              <span className="zulu-plugin__label-format-copy">
                <strong>
                  {option.title}
                  {option.recommended ? (
                    <span className="zulu-plugin__label-rec">Recommended</span>
                  ) : null}
                </strong>
                <small>{option.subtitle}</small>
              </span>
            </label>
          );
        })}
      </div>
      {status === "saving" ? (
        <MerchantLoader size="inline" label="Saving label format…" />
      ) : null}
      {status === "saved" ? (
        <p className="zulu-plugin__settings-status is-ok">Saved. New labels use this size.</p>
      ) : null}
      {status === "error" ? (
        <p className="zulu-plugin__settings-status is-error">Could not save. Try again.</p>
      ) : null}
    </>
  );
};

export default LabelSizePicker;
